import * as React from 'react'
import * as Redux from 'react-redux'
import styled from 'styled-components'

import { t, tn } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import * as hooks from '@owl-nest/hooks'
import { Status } from '@owl-nest/redux-wrapper'
import * as service from '@owl-nest/services'
import * as api from '@owl-nest/api-client/latest'
import { BREAKPOINTS, Button, styles as S } from '@ulule/owl-kit-components/next'

import { loadProjectFans } from '../../../../../actions/fandom'
import * as fanModel from '../../../../models/fan'
import { RootState, AppState } from '../../../../../reducers'
import { useListLoadingIndicator } from '../../hooks/useListLoadingIndicator'

import { Backer } from '../../components/Backer'
import { SidebarNewsCTA, SidebarRewardsCTA } from '../../components/Sidebar'
import { Follower } from './Follower'
import { Sponsor } from './Sponsor'

import { DisplayMode } from '../../../../context'
import * as Styles from '../../style'
import { TRACKING_SOURCE } from '../../utils/trackerEnums'

import type { ExtraFields } from '../../../../models/project'

export function BackersTab(): React.ReactElement<unknown> {
  const dispatch = Redux.useDispatch()
  const { fandom, project } = Redux.useSelector<RootState, AppState>((state) => state.application)
  const { orders_count: ordersCount, supporters_count: supportersCount, sponsorships } = project.data
  const fansCount = fandom.count

  const isEditMode = React.useContext(DisplayMode) === 'edit'

  const [isLoadingFans, markLoadingStartFans] = useListLoadingIndicator(fandom.fans.length)

  const loadMoreFans = (): void => {
    if (fandom.next) {
      dispatch(loadProjectFans(project.data.id, { pagination: fandom.next, extraFields: [...fanModel.extraFields] }))
      markLoadingStartFans()
    }
  }

  React.useEffect(() => {
    if (fandom.status.load === Status.PRISTINE) {
      dispatch(
        loadProjectFans(project.data.id, {
          pagination: '?limit=9',
          extraFields: [...fanModel.extraFields],
          forceClean: true,
        }),
      )
    }
  }, [])

  return (
    <Wrapper>
      <Styles.Main $isEditMode={isEditMode}>
        <Styles.Wrapper $isEditMode={isEditMode}>
          <Styles.Content $isEditMode={isEditMode}>
            <S.heading.M>
              {tn(
                '%(ordersCount)d contribution to this project',
                '%(ordersCount)s contributions to this project',
                ordersCount || supportersCount,
                { ordersCount: ordersCount || supportersCount },
              )}
            </S.heading.M>
            {sponsorships?.map((sponsorship, i) => {
              if (sponsorship.amount === 0) return
              return <Sponsor key={i} sponsorship={sponsorship} project={project.data} />
            })}
            <ProjectOrderList />
          </Styles.Content>
          <Styles.Sidebar $isEditMode={isEditMode}>
            {!models.project.isDonationBased(project.data) && <SidebarRewardsCTA />}
            <SidebarNewsCTA source={TRACKING_SOURCE.BACKERS} />
          </Styles.Sidebar>
        </Styles.Wrapper>
      </Styles.Main>
      <Styles.Main>
        <Styles.Wrapper>
          <FollowerContainer>
            <S.heading.M>
              {tn(
                '%(fansCount)d person is following this project',
                '%(fansCount)d people are following this project',
                fansCount,
                { fansCount },
              )}
            </S.heading.M>
            <Columns>
              {fandom?.fans.map((follower, i) => {
                return <Follower key={i} follower={follower} project={project.data} />
              })}
            </Columns>
            {fandom?.next && (
              <ShowMoreContainer>
                <Button loading={isLoadingFans} kind="secondary" onClick={() => loadMoreFans()}>
                  {t('See more')}
                </Button>
              </ShowMoreContainer>
            )}
          </FollowerContainer>
        </Styles.Wrapper>
      </Styles.Main>
    </Wrapper>
  )
}

function ProjectOrderList() {
  const { project } = Redux.useSelector<RootState, AppState>((state) => state.application)

  const useProjectOrder = service.order.useProjectOrders(project.data.id, {
    params: {
      limit: 10,
      extraFields: ['latest_user_project_comment'],
    },
    fetchOnMount: true,
  })
  const orderPageList = hooks.data(useProjectOrder.response)

  return (
    <>
      {orderPageList?.map((page) => {
        return page.orders.map((order) => {
          return (
            order.user && (
              <Backer
                key={order.id}
                supporter={{
                  ...order.user,
                  latest_project_comment: order.latest_user_project_comment ?? order.user.latest_project_comment,
                  latest_project_order: order as api.Order,
                }}
                project={project.data}
                source={TRACKING_SOURCE.BACKERS}
              />
            )
          )
        })
      })}
      {useProjectOrder.meta?.next && (
        <ShowMoreContainer>
          <Button
            kind="secondary"
            loading={useProjectOrder.response.status === hooks.QueryStatus.PENDING}
            onClick={() => useProjectOrder.queryNext()}
          >
            {t('See more')}
          </Button>
        </ShowMoreContainer>
      )}
    </>
  )
}

const Wrapper = styled.div`
  padding: 1px 0;

  ${S.heading.M} {
    display: block;
    margin-bottom: 25px;
  }

  ${Styles.Content} {
    padding: 0 10px 1px;
    width: 100%;
  }

  @media not screen and ${BREAKPOINTS.MOBILE_L} {
    ${Styles.Wrapper} {
      padding: 0;
    }

    ${Styles.Content} {
      padding: 0;

      ${S.heading.M} {
        margin: 0 20px 25px;
      }
    }
  }

  @media screen and ${BREAKPOINTS.LAPTOP} {
    ${S.heading.M} {
      margin-bottom: 40px;
    }
  }
`

const ShowMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
  padding: 20px 0 0;

  @media not screen and ${BREAKPOINTS.MOBILE_L} {
    padding-bottom: 0;

    ${S.button.Button} {
      margin-left: 10px;
      margin-right: 10px;
      width: 100%;
    }
  }
`

const FollowerContainer = styled.div`
  flex-grow: 1;
  padding: 0 10px;

  @media not screen and ${BREAKPOINTS.MOBILE_L} {
    ${S.heading.M} {
      padding: 0 10px;
    }
  }
`

const Columns = styled.div`
  display: block;
  flex-wrap: wrap;

  & > div {
    flex: 0 0 33.3333%;
    box-sizing: border-box;
  }

  @media screen and ${BREAKPOINTS.LAPTOP} {
    display: flex;
  }
`
